import { afterNextRender, Directive, ElementRef, inject, input, OnDestroy, Renderer2 } from '@angular/core';

/**
 * Scroll reveal without a library. Critical detail for SEO: on the server and
 * during prerender this directive does nothing, so crawlers receive fully
 * visible content — never opacity:0. Same for prefers-reduced-motion.
 */
@Directive({ selector: '[appReveal]' })
export class Reveal implements OnDestroy {
  readonly revealDelay = input(0);

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private observer?: IntersectionObserver;

  constructor() {
    // afterNextRender only ever runs in the browser.
    afterNextRender(() => {
      const node = this.el.nativeElement;
      if (typeof IntersectionObserver === 'undefined') { return; }
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { return; }

      this.renderer.setStyle(node, 'opacity', '0');
      this.renderer.setStyle(node, 'transform', 'translateY(18px)');
      this.renderer.setStyle(node, 'transition', 'opacity .8s ease, transform .8s ease');
      this.renderer.setStyle(node, 'transition-delay', `${this.revealDelay()}ms`);

      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) { continue; }
          this.renderer.setStyle(node, 'opacity', '1');
          this.renderer.setStyle(node, 'transform', 'none');
          this.observer?.unobserve(node);
        }
      }, { threshold: 0.12, rootMargin: '0px 0px -40px' });

      this.observer.observe(node);
    });
  }

  ngOnDestroy(): void { this.observer?.disconnect(); }
}
